import { Parse } from '@cadstrom/cadstrom-shared';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import { isValidDistance } from '../../status-bar/grid-utils.js';
import { CadRendererSnapshotEvent, CadRendererSyncNamespace } from '../broadcast-manager.js';
import { useSyncSnapshotHydration, useSyncSnapshotResponder } from '../sync-core.js';
import { arrayKeepingValid, pickLatestByKey } from '../sync-protocol.js';

import type { DrawingManager, IGridWithUnits } from '@cadstrom/renderer';
import type { CadTool, ModelGrid, Utils } from '@cadstrom/sigma-engine-shared';
import type { CadRendererSyncCore } from '../sync-core.js';

export type CadRendererGridType = NonNullable<IGridWithUnits['style']> | 'none';

export const CadRendererGridTypeSchema = z.enum([
	'line',
	'dot',
	'dash',
	'plus',
	'none',
]) satisfies Utils.Zot<CadRendererGridType>;

/** How the grid is drawn, independent of the design's grid geometry. */
export interface CadRendererGridAppearance {
	gridType: CadRendererGridType;
	spacingPercent: number;
	alphaPercent: number;
}

export const CadRendererGridAppearanceSchema = z.object({
	gridType: CadRendererGridTypeSchema,
	spacingPercent: z.number(),
	alphaPercent: z.number(),
}) satisfies Utils.Zot<CadRendererGridAppearance>;

/** Everything needed to draw one design's grid: its geometry plus the grid appearance. */
export interface CadRendererGridSettings extends CadRendererGridAppearance {
	distance: number;
	units: ModelGrid['units'];
}

/** Synchronized grid geometry for one design. */
export type CadRendererGridState = {
	designId: string;
	distance: number;
	units: ModelGrid['units'];
	updatedAt: number;
	sourceWindowId: string;
};

const CadRendererGridStateSchema = z.object({
	designId: z.string(),
	distance: z.number(),
	units: z.enum(['mm', 'microns', 'inch', 'mil']),
	updatedAt: z.number(),
	sourceWindowId: z.string(),
}) satisfies Utils.Zot<CadRendererGridState>;

export type GridSnapshotPayload = {
	requestId?: string;
	gridStates: readonly CadRendererGridState[];
};

const GridSnapshotPayloadSchema = z.object({
	requestId: z.string().optional(),
	gridStates: arrayKeepingValid(CadRendererGridStateSchema),
}) satisfies Utils.Zot<GridSnapshotPayload>;

export const GRID_DEFAULT_TYPE: CadRendererGridType = 'dot';
export const GRID_DEFAULT_SPACING_PERCENT = 1.0;
export const GRID_DEFAULT_ALPHA_PERCENT = 1.0;
export const GRID_MAJOR_EVERY = 10;

/** Grid line width, in CSS pixels, for every style that draws lines. */
const GRID_LINE_SIZE = 1;
/** Dot diameter, and half the arm length of crosses, in CSS pixels. */
const GRID_DOT_SIZE: Record<'dot' | 'plus', number> = { dot: 1, plus: 3 };

/** The grid style each authoring tool shows by default. */
const CAD_TOOL_GRID_TYPES: Record<CadTool, CadRendererGridType> = {
	kicad: 'dot',
	altium: 'line',
	orcad: 'dot',
	eagle: 'none',
	allegro: 'line',
	xpedition: GRID_DEFAULT_TYPE,
	cadstrom: GRID_DEFAULT_TYPE,
};

type GridDrawingManagerRegistration = {
	modelGrid: ModelGrid;
	drawingManager: DrawingManager;
};

/**
 * Picks the grid style a project's authoring tool shows by default.
 *
 * @param tool - The project's authoring tool, or `undefined` when unknown.
 * @returns The tool's default grid style, or the app's default when the tool is unknown.
 */
export function getDefaultGridTypeForCadTool(tool: CadTool | undefined): CadRendererGridType {
	return tool === undefined ? GRID_DEFAULT_TYPE : CAD_TOOL_GRID_TYPES[tool];
}

/**
 * Bridges grid display settings to the sync channel.
 *
 * It answers peer snapshot requests with current grid settings and applies the newest grid snapshot
 * per design when receiving live updates or initial hydration responses.
 */
export function useGridBridge({
	core,
	gridStates,
	updateGridState,
}: {
	core: CadRendererSyncCore;
	gridStates: Readonly<Record<string, CadRendererGridState>>;
	updateGridState: (state: CadRendererGridState, publish: boolean) => void;
}) {
	const gridStatesRef = useRef(gridStates);
	useEffect(() => {
		gridStatesRef.current = gridStates;
	}, [gridStates]);

	const createSnapshot = useCallback(
		(): GridSnapshotPayload => ({
			gridStates: Object.values(gridStatesRef.current),
		}),
		[],
	);

	useSyncSnapshotResponder({
		core,
		namespace: CadRendererSyncNamespace.Grid,
		createPayload: createSnapshot,
	});

	useEffect(() => {
		return core.subscribe(CadRendererSyncNamespace.Grid, (envelope) => {
			if (envelope.event !== CadRendererSnapshotEvent.Snapshot) {
				return;
			}

			const payload = parseGridSnapshot(envelope.payload);
			if (!payload) {
				return;
			}
			for (const state of payload.gridStates) {
				updateGridState(state, false);
			}
		});
	}, [core, updateGridState]);

	const applyHydrationResponses = useCallback(
		(responses: readonly GridSnapshotPayload[]) => {
			const states = responses.flatMap((payload) => payload.gridStates);
			for (const state of pickLatestByKey(states, (state) => state.designId)) {
				updateGridState(state, false);
			}
		},
		[updateGridState],
	);

	useSyncSnapshotHydration({
		core,
		namespace: CadRendererSyncNamespace.Grid,
		parsePayload: parseGridSnapshot,
		applyResponses: applyHydrationResponses,
	});
}

/**
 * Creates the grid geometry a design starts with before anyone changes it.
 *
 * @param designId - Design the state belongs to.
 * @param modelGrid - The design's own grid, whose distance and units become the defaults.
 * @param sourceWindowId - Window recorded as the state's author.
 * @param updatedAt - Version timestamp; 0 lets any real update win.
 * @returns The default grid state for the design.
 */
export function createDefaultGridState(
	designId: string,
	modelGrid: ModelGrid,
	sourceWindowId: string,
	updatedAt: number,
): CadRendererGridState {
	return {
		designId,
		distance: modelGrid.distance,
		units: modelGrid.units,
		updatedAt,
		sourceWindowId,
	};
}

/**
 * Creates the grid appearance used when nothing has been chosen yet.
 *
 * @param gridType - Grid style to start from. Defaults to dots.
 * @returns The default appearance for the given style.
 */
export function createDefaultGridAppearance(
	gridType: CadRendererGridType = GRID_DEFAULT_TYPE,
): CadRendererGridAppearance {
	return {
		gridType,
		spacingPercent: GRID_DEFAULT_SPACING_PERCENT,
		alphaPercent: GRID_DEFAULT_ALPHA_PERCENT,
	};
}

/**
 * Draws a design's grid in its drawing manager, or removes it when the grid is turned off. Invalid
 * distances leave the current grid in place.
 *
 * @param registration - The design's model grid and drawing manager.
 * @param state - The grid geometry and appearance to draw.
 */
export function applyGridStateToDrawingManager(
	registration: GridDrawingManagerRegistration,
	state: CadRendererGridSettings,
): void {
	if (state.gridType === 'none') {
		registration.drawingManager.unrenderGrid();
		return;
	}

	if (!isValidDistance(state.distance, state.units) || !registration.drawingManager.isGridSupported) {
		return;
	}

	registration.drawingManager.renderGrid({
		unitSize: Parse.Util.convertGridDistanceUnits(state.distance, state.units, 'mm'),
		multiple: registration.modelGrid.multiple ?? 1,
		style: state.gridType,
		spacingPercent: state.spacingPercent,
		lineSize: GRID_LINE_SIZE,
		dotSize: state.gridType === 'dot' || state.gridType === 'plus' ? GRID_DOT_SIZE[state.gridType] : undefined,
		majorEvery: GRID_MAJOR_EVERY,
		opacity: state.alphaPercent,
		distance: state.distance,
		units: state.units,
		invertX: registration.modelGrid.invertX,
		invertY: registration.modelGrid.invertY,
	});
}

function parseGridSnapshot(value: unknown): GridSnapshotPayload | null {
	const result = GridSnapshotPayloadSchema.safeParse(value);
	return result.success ? result.data : null;
}
